import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { generateMonthOptions, parseMonthValue } from '../utils/dateUtils';

const useSalesTransactionsData = ({ salesTransactionsUrl, getAuthHeaders, showSuccessMessage }) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');

  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [selectedChannel, setSelectedChannel] = useState('all');

  const fetchTransactions = useCallback(async () => {
    setIsLoadingTransactions(true);
    setTransactionsError('');
    try {
      const config = await getAuthHeaders();
      const monthParams = parseMonthValue(selectedMonth);
      const params = monthParams ? { year: monthParams.year, month: monthParams.month } : {};
      if (selectedChannel && selectedChannel !== 'all') {
        params.salesChannelId = selectedChannel;
      }
      const response = await axios.get(salesTransactionsUrl, { ...config, params });
      if (response.data.success) {
        setTransactions(response.data.data.transactions || []);
      } else {
        setTransactionsError(response.data.error || 'Failed to fetch transactions');
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactionsError('Failed to load sales transactions');
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [salesTransactionsUrl, getAuthHeaders, selectedMonth, selectedChannel]);

  const updateTransactionDate = async (transactionId, newDate) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.put(
        `${salesTransactionsUrl}/${transactionId}`,
        { saleRecordedDate: newDate },
        config
      );
      if (response.data.success) {
        setTransactions(prev =>
          prev.map(t => t.id === transactionId ? response.data.data : t)
        );
        showSuccessMessage('Transaction date updated successfully!');
        return true;
      } else {
        setTransactionsError(response.data.error || 'Failed to update transaction date');
        return false;
      }
    } catch (err) {
      console.error('Error updating transaction date:', err);
      setTransactionsError(err.response?.data?.error || 'Failed to update transaction date');
      return false;
    }
  };

  return {
    transactions,
    isLoadingTransactions,
    transactionsError,
    fetchTransactions,
    updateTransactionDate,
    selectedMonth,
    setSelectedMonth,
    monthOptions,
    selectedChannel,
    setSelectedChannel,
  };
};

export default useSalesTransactionsData;
